<template>
  <div>
    <label for="email">
      Email
    </label>
    <v-text-field
      id="email"
      v-model="form.email"
      :error-messages="errors.email"
      required
      type="email"
      variant="outlined"
      autofocus
      @change="validateEmail"
      @keydown.enter="submit"
    />
    <div class="d-flex justify-lg-space-between">
      <label for="password">
        Password
      </label>
      <v-spacer />
      <router-link
        :to="'/forgot-password?email=' + form.email"
        class="text-right text-decoration-none text-primary"
      >
        Forgot password?
      </router-link>
    </div>
    <v-text-field
      id="password"
      v-model="form.password"
      :error-messages="errors.password"
      type="password"
      required
      variant="outlined"
      @change="validatePassword"
      @keydown.enter="submit"
    />
    <v-btn
      class="action mt-1 animate-btn"
      variant="outlined"
      :disabled="submitLock"
      @click="submit"
    >
      Login
    </v-btn>
    <v-row
      no-gutters
      class="my-6 divider-row align-center"
    >
      <v-divider /><span class="px-2">OR</span><v-divider />
    </v-row>
    <v-row
      no-gutters
      class="mt-2 justify-space-between"
    >
      <microsoft-button
        @success="onAuthSuccess"
        @error="setError"
      />
      <google-button
        class="pt-6"
        @success="onAuthSuccess"
        @error="setError"
      />
    </v-row>
  </div>
</template>

<script>
import { getModuleFromPath } from '@/utils'
import GoogleButton from '@/components/Users/GoogleButton.vue'
import { useGeneralStore } from '@/store/pinia/generalStore'
import { isRedirectionForDocs, redirectToDocs } from '@/auth/redirectionForDocs'

import { defineAsyncComponent } from 'vue'

const MicrosoftButton = defineAsyncComponent(() => import('@/components/Users/MicrosoftButton.vue'))

export default {
  name: 'LoginForm',
  components: { GoogleButton, MicrosoftButton },
  props: {
    errorMessage: {
      type: String,
      default: null
    },
    successMessage: {
      type: String,
      default: null
    }
  },
  emits: ['update:errorMessage'],
  data () {
    return {
      errors: { email: '', password: '' },
      form: { email: '', password: '' },
      submitLock: false,
    }
  },
  methods: {
    isValidEmail (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    validateEmail () {
      if (!this.form.email) {
        this.errors.email = 'Email is required!'
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Email is not valid!'
      } else {
        this.errors.email = ''
      }
    },
    validatePassword () {
      if (!this.form.password) {
        this.errors.password = 'Password is required!'
      } else {
        this.errors.password = ''
      }
    },
    validateForm () {
      // Email validation
      this.validateEmail()
      // Password validation
      this.validatePassword()
      return !this.errors.email && !this.errors.password
    },
    setError (error) {
      this.$emit('update:errorMessage', error)
    },
    async onAuthSuccess () {
      try {
        await this.$auth.refreshAppConfig()

        // Set post-login loading state
        const generalStore = useGeneralStore()
        generalStore.setShowLoadingLayout(true)

        let lastVisitedModule = localStorage.getItem('lastVisitedModule') || 'stock'

        const redirect = this.$route.query['redirect']?.toString() || ''
        if (isRedirectionForDocs(redirect)) {
          return redirectToDocs(redirect)
        } else if (redirect) {
          lastVisitedModule = getModuleFromPath(redirect) || lastVisitedModule
          if(redirect.startsWith('/buy/home')) {
            this.$router.push({ path: '/buy/shopping' })
          }else {
            this.$router.push({path: redirect})
          }
        } else {
          this.$router.push({path: `/${lastVisitedModule || 'stock'}/home`})
        }

        const defaultCurrency = generalStore.buySettings?.default_currency
        localStorage.setItem('defaultCurrency', defaultCurrency || null)
        localStorage.setItem('lastVisitedModule', lastVisitedModule)
      } catch (e) {
        console.error(e)
      }
    },
    async submit () {
      // To avoid double submit
      if (this.submitLock) {
        return
      }
      this.setError(null)
      if (!this.validateForm()) {
        return
      }

      this.submitLock = true
      try {
        // All validations passed, so try to log in
        const response = await this.$auth.login(this.form.email.toLowerCase(), this.form.password)
        if (response.status === 200) {
          await this.onAuthSuccess()
        } else {
          this.setError('Either the password or the email address is incorrect.')
        }
      } catch {
        this.setError('Something went wrong, please try again later.')
      }
      this.submitLock = false
    },
  },

}
</script>
